import { QuoteCreationParams } from '@brenger/api-client';
import { AnyAction, TaskAbortError } from '@reduxjs/toolkit';
import { clarity } from '../../../configs/clarity';
import { AppListenerApi, startAppListening } from '../../../state/middlewares/listener';
import { logError } from '../../../utils/basics';
import { priceClient } from '../../../utils/request';
import { actions, getPriceRequestParams } from '../ducks';
import { GeneralFlowActionTypes } from '../typings';

const DRAFT_TRIGGER_TYPES: string[] = [
  GeneralFlowActionTypes.SET_DESTINATION_DETAILS,
  GeneralFlowActionTypes.SET_CHOICE,
];

/**
 * Asks for a draft quote while there is not enough input for a real price (same behavior as the former triggerDraft saga).
 */
export function triggerDraft(action: AnyAction, api: AppListenerApi): void {
  const isDestinationChange = action.type === GeneralFlowActionTypes.SET_DESTINATION_DETAILS;
  const isChoiceChange = action.type === GeneralFlowActionTypes.SET_CHOICE;
  if (!isDestinationChange && !isChoiceChange) {
    // This change is not happening in the general flow, or further down the general flow
    return;
  }
  const params = getPriceRequestParams(api.getState());
  if (params !== null) {
    // it means we have a valid quote, so we are able to display a price to the customer
    // no need anymore to cursor-pointer trigger draft versions
    return;
  }
  api.dispatch(actions.postDraftQuote());
}

/**
 * Posts a draft quote (same behavior as the former postDraft saga).
 * A draft replaced by a later POST_DRAFT_QUOTES stops at its pending call.
 */
export async function postDraft(api: AppListenerApi): Promise<void> {
  try {
    const draftParams = getPriceRequestParams(api.getState(), { isDraft: true }) as QuoteCreationParams;
    clarity.setTag('price_draft');
    await api.pause(priceClient.quotes.createDraft(draftParams));
  } catch (e) {
    if (e instanceof TaskAbortError) return;
    logError(e);
  }
}

/**
 * Registers the GeneralFlow draft quote side effects (same behavior as the former draftQuotesSaga watchers).
 */
export const startDraftQuoteListeners = (): void => {
  /* One debounce (2.5s) over the destination and choice changes */
  startAppListening({
    predicate: action => DRAFT_TRIGGER_TYPES.includes(action.type),
    effect: async (action, api) => {
      api.cancelActiveListeners();
      await api.delay(2500);
      triggerDraft(action, api);
    },
  });

  startAppListening({
    type: GeneralFlowActionTypes.POST_DRAFT_QUOTES,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      await postDraft(api);
    },
  });
};
